import React, { useState, useCallback, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { useToast } from "@chakra-ui/react";
import imageCompression from "browser-image-compression";

const IMAGE_COMPRESSION_OPTIONS = {
  maxSizeMB: 0.3,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
};
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const MAX_FILES = 20;
const MAX_FILE_SIZE_LABEL = "10 MB";

const useWindowSize = () => {
  const [windowSize, setWindowSize] = React.useState({
    width: undefined,
    height: undefined,
  });

  React.useEffect(() => {
    const handleResize = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return windowSize;
};

const useDropZoneUpload = (initialPhotos = [], onDropAction = null) => {
  const [selectedFiles, setSelectedFiles] = useState(initialPhotos);
  const [isCompressing, setIsCompressing] = useState(false);
  const toast = useToast();
  const fileCountRef = useRef(initialPhotos.length);

  const showWarning = useCallback((title, description) => {
    toast({ title, description, status: "warning", duration: 5000, isClosable: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDrop = useCallback(async (acceptedFiles) => {
    const activeCount = fileCountRef.current;
    const slotsLeft = Math.max(0, MAX_FILES - activeCount);

    if (slotsLeft === 0) {
      showWarning("Too many files", `Maximum ${MAX_FILES} files allowed. Remove some before adding new ones.`);
      return;
    }

    const filesToProcess = acceptedFiles.slice(0, slotsLeft);
    const overflowCount = acceptedFiles.length - filesToProcess.length;

    if (overflowCount > 0) {
      showWarning("Too many files", `Only ${slotsLeft} more file(s) allowed. ${overflowCount} file(s) were not added.`);
    }

    setIsCompressing(true);
    try {
      const processed = await Promise.all(
        filesToProcess.map(async (file) => {
          if (file.type.startsWith("image/")) {
            const compressed = await imageCompression(file, IMAGE_COMPRESSION_OPTIONS);
            return new File([compressed], file.name, { type: file.type });
          }
          return file;
        })
      );

      setSelectedFiles((prev) => {
        const updated = [
          ...prev,
          ...processed.map((file) => ({
            id: null,
            url: URL.createObjectURL(file),
            file: file,
            name: file.name,
            fileType: file.type.split("/")[1],
          })),
        ];
        fileCountRef.current = updated.filter((f) => !f._destroy).length;
        return updated;
      });
      onDropAction?.();
    } finally {
      setIsCompressing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDropRejected = useCallback((rejections) => {
    const tooLarge = [];
    rejections.forEach(({ file, errors }) => {
      errors.forEach((err) => {
        if (err.code === "file-too-large") tooLarge.push(file.name);
      });
    });

    if (tooLarge.length > 0) {
      showWarning("File too large", `${tooLarge.join(", ")} — max ${MAX_FILE_SIZE_LABEL} per file.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanBuffer = () => {
    setSelectedFiles([]);
    fileCountRef.current = 0;
  };

  const handleRemoveFile = (index, id) => {
    const updatedFiles = [...selectedFiles];
    if (id) {
      updatedFiles[index] = { id: updatedFiles[index].id, _destroy: true };
    } else updatedFiles.splice(index, 1);
    setSelectedFiles(updatedFiles);
    fileCountRef.current = updatedFiles.filter((f) => !f._destroy).length;
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    onDropRejected,
    accept: { "image/*": [".jpeg", ".png"], "application/pdf": [".pdf"] },
    multiple: true,
    maxSize: MAX_FILE_SIZE_BYTES,
  });

  return {
    selectedFiles,
    cleanBuffer,
    getRootProps,
    getInputProps,
    handleRemoveFile,
    isCompressing,
  };
};

export { useWindowSize, useDropZoneUpload };
